import { mapFirebaseUrlToProxy } from "~/utils/imageUrl";

/* ====================================================================
 *  galleryImages — які кадри показує галерея товару (п.9г).
 *
 *  Чиста функція без Firebase SDK: до п.9г галерея резолвила кожен кадр
 *  через `getDownloadURL`, тобто вміла працювати ТІЛЬКИ в клієнті — у
 *  сирому HTML сторінки товару не було жодного `<img>`, а LCP-кадр
 *  стартував аж після гідрації і N запитів у Storage API. Шляхи
 *  `products/…` і так лежать у документах, тож проксі `/api/img/` дає
 *  той самий результат синхронно і на сервері.
 *
 *  ПОРЯДОК ПЕРШОГО КАДРУ КРИТИЧНИЙ. На сервері варіант приходить із
 *  `variantsLite`, де картинка одна — поле `image`, яке тригер
 *  `aggregateProductFromVariants` (functions/src/products.js) склав як
 *  `mainImagePath → mainImageUrl → image → images[0]`. У клієнті той
 *  самий варіант приїжджає повним документом і кадрів стає N. Якщо
 *  перший кадр порахувати іншою формулою, LCP-картинка підмінялась би
 *  на очах після гідрації — тому `FIRST_KEYS` дзеркалить саме той
 *  пріоритет, а решта кадрів лише дописується слідом.
 * ==================================================================== */

type AnyRecord = Record<string, unknown>;

/**
 * Пріоритет головного кадру — ДЗЕРКАЛО `getVariantImage` у
 * functions/src/products.js: mainImagePath → mainImageUrl → image →
 * images[0]. Останній щабель саме `images[0]`, а не «перший що знайдеться
 * у списках»: інакше для варіанта без іменованих полів сервер (lite)
 * узяв би images[0], а клієнт (повний документ) — imagePaths[0], і
 * LCP-кадр підмінявся б після гідрації.
 */
const VARIANT_NAMED_KEYS = ["mainImagePath", "mainImageUrl", "image"] as const;

/** Те саме для товару — дзеркало `getProductImage`. */
const PRODUCT_NAMED_KEYS = [
  "coverImageUrl",
  "mainImagePath",
  "mainImageUrl",
  "image",
] as const;

/** Списки додаткових кадрів; порядок сталий: шляхи, потім готові URL. */
const REST_KEYS = ["imagePaths", "images"] as const;

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function firstOfArray(src: AnyRecord, key: string): string {
  const list = src[key];
  return Array.isArray(list) ? str(list[0]) : "";
}

function refsOf(
  src: AnyRecord | null | undefined,
  namedKeys: readonly string[],
): string[] {
  if (!src) return [];
  const out: string[] = [];

  const first =
    namedKeys.map((k) => str(src[k])).find(Boolean) ||
    firstOfArray(src, "images");
  if (first) out.push(first);

  for (const k of REST_KEYS) {
    const list = src[k];
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const v = str(item);
      if (v) out.push(v);
    }
  }

  return out;
}

/**
 * Кадри галереї як проксі-URL, у порядку показу.
 * Варіант має пріоритет; товар — фолбек, коли у варіанта картинок немає
 * зовсім (а не «на додачу»: домішувати чужі кадри до варіанта не можна,
 * галерея показує саме обраний лот).
 */
export function selectGalleryImages(
  product: AnyRecord | null | undefined,
  variant: AnyRecord | null | undefined,
): string[] {
  const refs = refsOf(variant, VARIANT_NAMED_KEYS);
  const source = refs.length ? refs : refsOf(product, PRODUCT_NAMED_KEYS);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const ref of source) {
    const url = mapFirebaseUrlToProxy(ref);
    // Дедуп саме по URL, а не по вихідному значенню: той самий кадр
    // приходить і шляхом (mainImagePath), і в imagePaths.
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}
